<script lang="ts">
    import Input from "../../../components/formComponents/Input.svelte"
    import { isValidLangOrLatCoordinate, onChange, validateField } from "../../../components/formComponents/helper"
    import Map from "../../../components/formComponents/Map.svelte"
    import { isMobile, newNotification } from "../../../store"
    import Modal from "../../../components/Modal.svelte"
    import LoadingWrapper from "../../widgets/LoadingWrapper.svelte"
    import Icon from "../../../components/Icon.svelte"
    import { mdiCameraMarker } from "@mdi/js"
    import Cta from "../../../components/CTA.svelte"
    import { getDBEntry, putDBEntry } from "../../../../api"
    import { createEventDispatcher } from "svelte"
    import { locationStore } from "../../widgets/location/locationStore"
    export let mediaEntry: Media
    const dispatch = createEventDispatcher()
    let manualLocationInput: LocationObj = {
        latitude: "",
        longitude: "",
    }
    let modalStates = {
        showLocationModal: true,
        showExifInputModal: false,
        showMapModal: false,
        isSettingExif: false,
    }

    function closeModals() {
        modalStates = {
            showLocationModal: false,
            showExifInputModal: false,
            showMapModal: false,
            isSettingExif: false,
        }
    }

    const handleExifInput = async () => {
        modalStates.isSettingExif = true
        const validations = [
            { id: "exifLat", value: String(manualLocationInput.latitude), validator: isValidLangOrLatCoordinate },
            { id: "exifLong", value: String(manualLocationInput.longitude), validator: isValidLangOrLatCoordinate },
        ]

        let isValid = true
        for (const { id, value, validator } of validations) {
            //@ts-ignore
            if (!validateField(id, value, validator)) isValid = false
        }
        if (!isValid) {
            newNotification({
                html: "Bitte geben Sie gültige Koordinaten ein.",
                class: "error",
            })
            modalStates.isSettingExif = false
            return
        }
        if (manualLocationInput.latitude && manualLocationInput.longitude) {
            await updateLocationInDB(
                Number(manualLocationInput.longitude),
                Number(manualLocationInput.latitude),
                mediaEntry.id
            )
        }
        closeModals()
    }

    const useCurrentLocationForImage = async () => {
        modalStates.isSettingExif = true

        try {
            // Get accurate position with a single watchPosition
            const position = $locationStore

            const lat = position.latitude
            const lng = position.longitude

            // Update the coordinates in the database
            await updateLocationInDB(lng, lat, mediaEntry.id)
            closeModals()
        } catch (error) {
            console.error("Error retrieving location:", error)
            newNotification({ html: "Error retrieving location", class: "error" })
        } finally {
            modalStates.isSettingExif = false
        }
    }

    const handleMapLocationSelect = (lat: number, long: number) => {
        updateLocationInDB(long, lat, mediaEntry.id).then(closeModals)
    }

    const updateLocationInDB = async (long: number, lat: number, id: string) => {
        const currentImage = await getDBEntry("media", { _id: id })
        if (!currentImage) {
            newNotification({
                html: "Bild nicht gefunden, bitte laden Sie die Seite neu.",
                class: "error",
            })
            closeModals()
            return
        }
        await putDBEntry("media", {
            ...currentImage,
            location: {
                latitude: lat,
                longitude: long,
            },
        })

        mediaEntry.location = {
            latitude: lat,
            longitude: long,
        }
        newNotification({
            html: "Standort erfolgreich angewandt!",
            class: "success",
        })
    }

    $: if (!modalStates.showLocationModal && !modalStates.showExifInputModal && !modalStates.showMapModal) {
        dispatch("locationSet")
    }
</script>

<Modal
    bind:show="{modalStates.showLocationModal}"
    size="xl"
    on:close="{() => {
        modalStates.showLocationModal = false
    }}"
>
    <h3
        class="label"
        slot="header"
    >
        <Icon
            path="{mdiCameraMarker}"
            size="1.2rem"
            color="#6C757D"
        />
        <p>Standortdaten verwenden</p>
    </h3>

    <p class="content-modal">
        Bitte wählen Sie aus, wie Sie den Standort für das Bild festlegen möchten. Sie können den Standort manuell in
        Koordinaten Form eingeben, den aktuellen Standort verwenden oder den Standort über die Karte auswählen. Bitte
        beachten Sie, das jegliche Änderungen hier umgehend gespeichert werden. Der Speicherprozess kann nicht
        rückgängig gemacht werden.
    </p>

    <div slot="footer">
        <LoadingWrapper styles="display: flex; gap: {$isMobile ? '0.6rem' : '1.2rem'};">
            <Cta
                type="tertiaryButton"
                icon="solar_pen-outline"
                content="{$isMobile ? 'Manuell' : 'Koordinaten Manuell angeben'}"
                on:click="{() => {
                    modalStates.showExifInputModal = true
                    modalStates.showLocationModal = false
                }}"
            />
            <Cta
                type="tertiaryButton"
                icon="solar_map-point-wave-outline"
                content="{$isMobile ? 'Aktuell' : 'Aktuellen Standort verwenden'}"
                on:click="{() => useCurrentLocationForImage()}"
            />
            <Cta
                type="tertiaryButton"
                content="{$isMobile ? 'Karte' : 'Standort über Karte auswählen'}"
                icon="solar_map-outline"
                on:click="{() => {
                    modalStates.showLocationModal = false
                    modalStates.showMapModal = true
                }}"
            />
        </LoadingWrapper>
    </div>
</Modal>

<Modal
    bind:show="{modalStates.showExifInputModal}"
    size="md"
    on:close="{() => {
        modalStates.showExifInputModal = false
    }}"
>
    <h3
        class="label"
        slot="header"
    >
        <Icon
            path="{mdiCameraMarker}"
            size="1.2rem"
            color="#6C757D"
        />
        <p>Koordinaten Manuell angeben</p>
    </h3>
    <div class="no-exif-found-modal-content content-modal">
        <LoadingWrapper
            active="{modalStates.isSettingExif}"
            styles=""
            classList="no-exif-found-modal-content"
        >
            <div style=" display: flex; flex-direction: column; gap: 0.5rem; margin-bottom: 0.5rem; width: 100%;">
                <Input
                    placeholder="Breitengrad (Lat)"
                    bind:value="{manualLocationInput.latitude}"
                    onChange="{onChange}"
                    id="exifLat"
                />

                <Input
                    placeholder="Längengrad (Long)"
                    bind:value="{manualLocationInput.longitude}"
                    onChange="{onChange}"
                    id="exifLong"
                />
            </div>
            <Cta
                icon="solar_diskette-outline"
                type="primaryButton"
                content="Speichern"
                on:click="{handleExifInput}"
            />
        </LoadingWrapper>
    </div>
    <div slot="footer">
        <LoadingWrapper
            active="{modalStates.isSettingExif}"
            styles="display: flex; gap:  {$isMobile ? '0.6rem' : '1.2rem'};"
        >
            <Cta
                icon="solar_diskette-outline"
                type="tertiaryButton"
                content="{$isMobile ? 'Aktuell' : 'Aktuellen Standort verwenden'}"
                on:click="{useCurrentLocationForImage}"
            />
            <Cta
                on:click="{() => {
                    modalStates.showMapModal = true
                    modalStates.showExifInputModal = false
                }}"
                content="{$isMobile ? 'Karte' : 'Standort über Karte auswählen'}"
                type="tertiaryButton"
                icon="solar_map-outline"
            />
        </LoadingWrapper>
    </div>
</Modal>

<Modal
    bind:show="{modalStates.showMapModal}"
    size="md"
    on:close="{() => {
        // set all modal states to false
        modalStates.showMapModal = false
        modalStates.showExifInputModal = false
        modalStates.showLocationModal = false
    }}"
>
    <h3
        class="label"
        slot="header"
    >
        <Icon
            path="{mdiCameraMarker}"
            size="1.2rem"
            color="#6C757D"
        />
        <p>Standort über Karte auswählen</p>
    </h3>
    <div class="modalMap content-modal">
        {#if modalStates.showMapModal}
            <Map
                mapId="modalMap"
                lat="{Number(mediaEntry.location.latitude)}"
                long="{Number(mediaEntry.location.longitude)}"
                on:locationSelected="{() => {
                    modalStates.showMapModal = false
                }}"
                onSelectLocation="{(lat, long) => {
                    handleMapLocationSelect(lat, long)
                }}"
            />
        {/if}
    </div>
</Modal>

<style lang="less">
    .modalMap:global {
        width: 100%;
        .mapwrapper {
            height: 400px !important;
            width: 100%;
        }
    }
</style>
